import { VehicleType } from '../../../resources/base/models/vehicleTypes';

/**
 * Builds a minimal CalculateSalesQuoteRequest payload for a new copied quote.
 *
 * Extracts only recalculation-relevant fields:
 *   - Accessories
 *   - Delivery costs
 *   - For USED: delivery date + Miles dealer vehicle price
 *
 * @param quoteData   Original quote response
 * @param vehicleType NEW / STOCK / USED
 * @returns Partial payload required for quote recalculation
 */

export const buildPayloadForNewCopiedQuote = (
  quoteData: QuoteSchema.Schemas.QuoteResponse,
  vehicleType: VehicleType
) => {
  const payload: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {};

  /**
   * ------------------------------------------------------------------
   * 1) ACCESSORIES (PR400001 and PR400002)
   * ------------------------------------------------------------------
   * Extracts option pricing from the vehicle model.
   * Only adds the accessory options if they contain a netAmount.
   */
  const pr400002 = quoteData.vehicleData?.model?.options?.find((options) => options.prKey === '400002');
  const pr400001 = quoteData.vehicleData?.model?.options?.find((options) => options.prKey === '400001');

  const modelOptions: QuoteSchema.Schemas.VehicleOptionCalculateQuoteRequest[] = [];

  if (pr400002?.optionPriceAmount?.netAmount) {
    modelOptions.push({
      prTypeId: '400002',
      isSelected: true,
      optionPriceAmount: {
        amount: pr400002.optionPriceAmount.netAmount,
        catalogVatCode: { enumId: '250174' },
      },
    });
  }

  if (pr400001?.optionPriceAmount?.netAmount) {
    modelOptions.push({
      prTypeId: '400001',
      isSelected: true,
      optionPriceAmount: {
        amount: pr400001.optionPriceAmount.netAmount,
        catalogVatCode: { enumId: '100639' },
      },
    });
  }

  if (modelOptions.length > 0) {
    payload.vehicleData = {
      ...(payload.vehicleData ?? {}),
      model: {
        ...(payload.vehicleData?.model ?? {}),
        options: modelOptions,
      },
    };
  }

  /**
   * ------------------------------------------------------------------
   * 2) DELIVERY COST COMPONENTS
   * ------------------------------------------------------------------
   * Only delivery cost items with specific tax codes are kept.
   * Ensures only components with a valid netAmount are pushed.
   */

  const deliveryComponents =
    quoteData.financialProduct?.offers?.[0]?.deliveryOptions?.[0]?.deliveryCostComponents
      ?.filter((component) =>
        ['NoTaxInland', 'FranceInputVATInland20'].includes(component.deliveryCostAmount?.taxCode ?? '')
      )
      .map((component) => ({
        isSelected: true,
        costType: 'DELIVERY' as QuoteSchema.Schemas.DeliveryCostType,
        deliveryCostAmount: {
          taxCode: component.deliveryCostAmount?.taxCode,
          netAmount: component.deliveryCostAmount?.netAmount,
        },
      })) ?? [];

  const validDeliveryComponents = deliveryComponents.filter((c) => c.deliveryCostAmount?.netAmount !== undefined);

  if (validDeliveryComponents.length > 0) {
    payload.financialProduct = {
      ...(payload.financialProduct ?? {}),
      offers: [
        {
          ...(payload.financialProduct?.offers?.[0] ?? {}),
          deliveryOptions: [{ deliveryCostComponents: deliveryComponents }],
        },
      ],
    };
  }

  /**
   * ------------------------------------------------------------------
   * 3) DELIVERY DATE (ONLY USED VEHICLES)
   * ------------------------------------------------------------------
   * For USED vehicles, copy the delivery date from the original contract.
   * For NEW or STOCK vehicles, the function returns here early.
   */
  if (vehicleType !== VehicleType.USED) return payload;

  const deliveryDate = quoteData.financialProduct?.contracts?.[0]?.deliveryData?.deliveryDate;

  if (deliveryDate) {
    payload.financialProduct = {
      ...(payload.financialProduct ?? {}),
      contracts: [
        {
          ...(payload.financialProduct?.contracts?.[0] ?? {}),
          deliveryData: { deliveryDate },
        },
      ],
    };
  }

  /**
   * ------------------------------------------------------------------
   * 4) MILES DEALER VEHICLE PRICE (ONLY USED VEHICLES)
   * ------------------------------------------------------------------
   * Adds the net vehicle price if available, used for recalculation.
   */
  const milesDealerVehiclePrice = quoteData.vehicleData?.milesDealerVehiclePrice?.grossAmount;

  if (milesDealerVehiclePrice) {
    payload.vehicleData = {
      ...(payload.vehicleData ?? {}),
      milesDealerVehiclePrice: {
        grossAmount: milesDealerVehiclePrice,
      },
    };
  }

  return payload;
};
